import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PLAN_SELECTION } from 'src/app/shared/constant/routes';
import { UserCodePopupComponent } from '../user-code-popup/user-code-popup.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TwaDetectionService, GooglePlayService, RequestService, LoaderService, ToastService } from 'src/app/shared/services';
import { Constants } from 'src/app/shared/constant/constants';

@Component({
  selector: 'app-subscription-expire-popup',
  templateUrl: './subscription-expire-popup.component.html',
  styleUrls: ['./subscription-expire-popup.component.scss'],
  standalone: true,
  imports: [TranslateModule],
})
export class SubscriptionExpirePopupComponent implements OnInit, OnDestroy {
  buySubscriptionUrl = PLAN_SELECTION.URL;
  @Input() headerText!: string;
  @Input() email!: string;
  @Input() password!: string;
  @Input() type!: boolean;

  private availablePlans: any[] = [];
  /** Stored promise from googlePlayService.init() so purchaseSimple can await it. */
  private googlePlayInitPromise: Promise<void> | null = null;

  constructor(
    private modalService: BsModalService,
    private router: Router,
    private translateService: TranslateService,
    private twaDetectionService: TwaDetectionService,
    private googlePlayService: GooglePlayService,
    private requestService: RequestService,
    private loader: LoaderService,
    private flashMessage: ToastService,
  ) {}

  toCancel() {
    this.modalService.hide();
  }

  ngOnInit(): void {
    console.log('[Popup:ngOnInit] ── Subscription expire popup opened');
    if (!this.headerText) {
      this.translateService.get('loginSubscriptionTrialExpired').subscribe((translation: string) => {
        this.headerText = translation;
      });
    }

    const isTwa = this.twaDetectionService.isTrustedWebActivity();
    console.log('[Popup:ngOnInit] isTWA:', isTwa);

    if (isTwa) {
      console.log('[Popup:ngOnInit] Step 1 — calling googlePlayService.init()');
      this.googlePlayInitPromise = this.googlePlayService.init();

      console.log('[Popup:ngOnInit] Step 2 — starting background ARC++ warmup');
      this.googlePlayService.startBackgroundWarmup();

      console.log('[Popup:ngOnInit] Step 3 — fetching Google Play plans');
      this.fetchGooglePlayPlans();
    } else {
      console.log('[Popup:ngOnInit] Not TWA — Google Play init skipped');
    }
  }

  private fetchGooglePlayPlans() {
    const url = `${Constants.ENDPOINTS.getPlans}/google`;
    console.log('[Popup:fetchPlans] Requesting plans from:', url);

    this.requestService.get(url).subscribe(
      (res: any) => {
        console.log('[Popup:fetchPlans] Response received:', res);
        if (res.data && res.data.length > 0) {
          this.availablePlans = res.data;
          console.log(`[Popup:fetchPlans] ${this.availablePlans.length} plan(s) loaded:`);
          this.availablePlans.forEach((plan, index) => {
            console.log(`  Plan ${index + 1}:`, {
              id: plan._id || plan.id,
              name: plan.planHeaderText,
              sku: plan.googlePlaySku,
              amount: plan.planAmount,
            });
          });
        } else {
          console.warn('[Popup:fetchPlans] No plans returned from API — res.data is empty or missing');
        }
      },
      error => {
        console.error('[Popup:fetchPlans] Failed to fetch plans:', {
          status: error.status,
          message: error.message,
          url: error.url,
        });
      },
    );
  }

  async toSubscription() {
    console.log('[Popup:toSubscription] ── Subscribe button tapped');
    const isTwa = this.twaDetectionService.isTrustedWebActivity();
    console.log('[Popup:toSubscription] isTWA:', isTwa);

    if (isTwa) {
      console.log('[Popup:toSubscription] Routing to Google Play purchase flow...');
      await this.initiateGooglePlayPurchase();
      console.log('[Popup:toSubscription] Purchase flow returned — hiding modal');
      this.modalService.hide();
    } else {
      console.log('[Popup:toSubscription] Web browser — navigating to plan selection:', this.buySubscriptionUrl);
      this.modalService.hide();
      this.router.navigate([this.buySubscriptionUrl]);
    }
  }

  private async initiateGooglePlayPurchase() {
    console.log('[Popup:purchase] ── initiateGooglePlayPurchase() entered');
    console.log('[Popup:purchase] Available plans count:', this.availablePlans.length);
    console.log('[Popup:purchase] Available plans:', this.availablePlans);

    try {
      // Step A: Find a valid plan
      const plan = this.availablePlans.find(p => p.googlePlaySku) || this.availablePlans[0];
      console.log(
        '[Popup:purchase] Step A — selected plan:',
        plan ? { id: plan._id || plan.id, sku: plan.googlePlaySku, name: plan.planHeaderText } : null,
      );

      if (!plan) {
        console.error('[Popup:purchase] Step A FAILED — no plans available at all');
        this.flashMessage.showError('No subscription plans available. Please try again later.');
        return;
      }

      if (!plan.googlePlaySku) {
        console.error('[Popup:purchase] Step A FAILED — plan has no googlePlaySku:', plan);
        this.flashMessage.showError('This plan is not available for Google Play. Please contact support.');
        return;
      }
      console.log('[Popup:purchase] Step A OK — SKU:', plan.googlePlaySku);

      // Step B: Call purchaseSimple — MUST run directly on user gesture
      console.log('[Popup:purchase] Step B — calling purchaseSimple() on user gesture (PaymentRequest.show will be called now)...');
      const t0 = Date.now();
      const { purchaseToken, response } = await this.googlePlayService.purchaseSimple({
        id: plan._id || plan.id,
        googlePlaySku: plan.googlePlaySku,
        stripePriceId: plan.stripePriceId,
        planAmount: plan.planAmount,
        planHeaderText: plan.planHeaderText,
      });
      console.log(`[Popup:purchase] Step B OK — purchaseSimple resolved in ${Date.now() - t0}ms, token: ${purchaseToken ? '(present)' : '(empty)'}`);

      // Step C: Start loader and verify with backend
      console.log('[Popup:purchase] Step C — starting loader and verifying purchase with backend...');
      this.loader.start();
      await this.verifyGooglePlayPurchase(purchaseToken, plan._id || plan.id);
      console.log('[Popup:purchase] Step C OK — backend verification successful');

      // Step D: Complete the PaymentResponse
      console.log('[Popup:purchase] Step D — calling response.complete("success")');
      await response.complete('success');
      console.log('[Popup:purchase] Step D OK — payment response completed');
    } catch (error: any) {
      console.error('[Popup:purchase] ── CAUGHT ERROR ──');
      console.error('[Popup:purchase] error.name:', error?.name);
      console.error('[Popup:purchase] error.message:', error?.message);
      console.error('[Popup:purchase] error.stack:', error?.stack);

      // ARC++ cold-start: Play Store service was asleep, first show() failed fast
      if (error?.message === 'BILLING_NOT_READY') {
        console.log('[Popup:purchase] → BILLING_NOT_READY: ARC++ cold-start. Showing retry toast.');
        this.flashMessage.showSuccess('Google Play is loading. Please tap Subscribe again.');
        return;
      }

      const isCancellation =
        error?.name === 'AbortError' ||
        error?.message?.includes('cancel') ||
        error?.message?.toLowerCase().includes('user activation') ||
        error?.message?.toLowerCase().includes('transient') ||
        error?.message?.toLowerCase().includes('delegated');

      if (isCancellation) {
        console.log('[Popup:purchase] → User cancelled the payment sheet — silent return');
        return;
      } else if (error?.message?.includes('not found') || error?.message?.includes('SKU')) {
        console.error('[Popup:purchase] → SKU not found on Play Store');
        this.flashMessage.showError('This subscription is not yet available in Google Play Store. Please try again later or contact support.');
      } else if (error?.message?.includes('not supported') || error?.message?.includes('not available')) {
        console.error('[Popup:purchase] → Billing not yet available');
        this.flashMessage.showError('Google Play Billing is still loading. Please wait a moment and try again.');
      } else {
        console.error('[Popup:purchase] → Unhandled error — showing generic toast');
        this.flashMessage.showError(error?.message || 'Purchase failed. Please try again.');
      }
    } finally {
      console.log('[Popup:purchase] finally — stopping loader');
      this.loader.stop();
    }
  }

  private verifyGooglePlayPurchase(purchaseToken: string, planId: string): Promise<void> {
    console.log('[Popup:verify] ── verifyGooglePlayPurchase() called');
    console.log('[Popup:verify] planId:', planId);
    console.log('[Popup:verify] purchaseToken:', purchaseToken ? '(present, length=' + purchaseToken.length + ')' : '(EMPTY)');
    console.log('[Popup:verify] email:', this.email || '(not set)');
    console.log('[Popup:verify] endpoint:', Constants.ENDPOINTS.saveSubscription);

    return new Promise((resolve, reject) => {
      const body: any = {
        purchaseToken: purchaseToken,
        planId: planId,
        email: this.email,
        paymentMethod: 'google_play',
      };
      console.log('[Popup:verify] POST body:', body);

      this.requestService.post(Constants.ENDPOINTS.saveSubscription, body).subscribe(
        (res: any) => {
          console.log('[Popup:verify] Backend verification SUCCESS:', res);
          this.loader.stop();
          this.flashMessage.showSuccess(this.translateService.instant('addSubscriptionPlanText'));
          console.log('[Popup:verify] Reloading page in 2 seconds...');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          resolve();
        },
        error => {
          this.loader.stop();
          console.error('[Popup:verify] Backend verification FAILED:', error);
          console.error('[Popup:verify] Error response body:', error.error);
          const errorMessage = error.error?.errorMessage || error.error?.message || error.message || 'Verification failed';
          console.error('[Popup:verify] Showing error:', errorMessage);
          this.flashMessage.showError(errorMessage);
          reject(error);
        },
      );
    });
  }

  ngOnDestroy(): void {
    console.log('[Popup:ngOnDestroy] ── popup destroyed (modal closed/dismissed) — stopping loader');
    this.loader.stop();
  }

  toUseCode() {
    this.toCancel();
    setTimeout(() => {
      const initialState = {
        email: this.email,
        password: this.password,
        type: this.type,
      };
      this.modalService.show(UserCodePopupComponent, {
        initialState,
      });
    }, 500);
  }
}
